import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  House, Package, GridFour, Sparkle, MagnifyingGlass, Upload, DownloadSimple,
  Plugs, GearSix, SignOut, Warehouse, Tag, ShoppingCart, Users, ChartBar, Bell, Robot,
} from "@phosphor-icons/react";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: House },
    ],
  },
  {
    label: "Catalog",
    items: [
      { to: "/products", label: "Products", icon: Package },
      { to: "/matrix", label: "Availability", icon: GridFour },
      { to: "/gap-finder", label: "Gap Finder", icon: MagnifyingGlass },
      { to: "/ai-builder", label: "AI Builder", icon: Sparkle },
      { to: "/jobs", label: "AI Jobs", icon: Robot },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/inventory", label: "Inventory", icon: Warehouse },
      { to: "/pricing", label: "Pricing", icon: Tag },
      { to: "/orders", label: "Orders", icon: ShoppingCart },
      { to: "/customers", label: "Customers", icon: Users },
    ],
  },
  {
    label: "Workflows",
    items: [
      { to: "/import", label: "Import", icon: Upload },
      { to: "/export", label: "Export", icon: DownloadSimple },
      { to: "/platforms", label: "Platforms", icon: Plugs },
      { to: "/analytics", label: "Analytics", icon: ChartBar },
      { to: "/notifications", label: "Notifications", icon: Bell },
    ],
  },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "superadmin";

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className="w-64 shrink-0 sticky top-0 h-screen flex flex-col text-white"
        style={{ background: "hsl(var(--sidebar-bg))" }}
      >
        <div className="px-5 py-5 border-b border-white/10 flex items-center gap-3">
          <div className="logo-mark">EK</div>
          <div className="min-w-0">
            <div className="font-display font-semibold text-[15px] leading-tight tracking-tight">EcomKit</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mt-0.5">Command Center</div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-2">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="mb-1">
              <div className="px-5 py-2 text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">
                {group.label}
              </div>
              {group.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`nav-${to.slice(1)}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-2 text-sm transition-colors relative ${
                      isActive
                        ? "text-white font-medium bg-white/[.06]"
                        : "text-white/70 hover:text-white hover:bg-white/[.04]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <span
                          className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r"
                          style={{ background: "hsl(var(--ecom-orange))" }}
                        />
                      )}
                      <Icon size={16} weight={isActive ? "fill" : "regular"} />
                      <span className="truncate">{label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          ))}

          {isAdmin && (
            <div className="mb-1 mt-2 border-t border-white/10 pt-2">
              <div className="px-5 py-2 text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">System</div>
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2 text-sm ${
                    isActive
                      ? "text-white font-medium bg-white/[.06]"
                      : "text-white/70 hover:text-white hover:bg-white/[.04]"
                  }`
                }
              >
                <GearSix size={16} />
                <span>Admin</span>
              </NavLink>
            </div>
          )}
        </nav>

        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-xs font-semibold shrink-0">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[11px] text-white/50 truncate font-mono-data">{user?.email}</div>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              title="Sign out"
              className="text-white/50 hover:text-white p-1.5 rounded"
            >
              <SignOut size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
          <div className="flex items-center justify-between gap-4 px-6 lg:px-8 h-14 min-w-0">
            <div className="flex items-center gap-3 min-w-0">
              <span className="pill pill-neutral">{user?.category || "General"}</span>
              <span className="text-sm text-muted-foreground truncate">Welcome back, <span className="text-foreground font-medium">{user?.name}</span></span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => nav("/notifications")} className="btn-ghost" data-testid="header-notifications-btn">
                <Bell size={16} />
              </button>
              <button onClick={() => nav("/platforms")} className="btn-primary" data-testid="header-connect-btn">
                + Connect Platform
              </button>
            </div>
          </div>
        </header>
        <div className="flex-1 min-w-0 px-6 lg:px-8 py-8">
          <div className="max-w-[1600px] mx-auto min-w-0">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
